import * as React from "react";

import { Badge, Button, Caption1, Checkbox, Input, Spinner, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

import { MatterCombobox } from "@features/matter";
import type { MatterListItem } from "@features/matter";

import { useIsWideLayout } from "@shared/ui/LayoutContext";
import { formatFileSize } from "@shared/lib/formatFileSize";
import { getAttachmentIcon } from "@shared/ui/attachmentIcon";

import type { AttachmentRowData } from "./models";

interface AttachmentRowProps {
  row: AttachmentRowData;
  matters: MatterListItem[];
  mattersLoading?: boolean;
  disabled?: boolean;
  onToggle: (id: string, selected: boolean) => void;
  onNameChange: (id: string, name: string) => void;
  onMatterOverrideChange: (id: string, matterId: number | null) => void;
  onRetry: (id: string) => void;
}

const useStyles = makeStyles({
  row: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  rowWide: {
    display: "grid",
    gridTemplateColumns: "1.4fr 1fr 1fr",
    columnGap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
  rowUnchecked: {
    opacity: 0.55,
  },
  top: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  topWide: {
    minWidth: 0,
  },
  iconBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "30px",
    height: "30px",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
    flexShrink: 0,
  },
  meta: {
    minWidth: 0,
    flexGrow: 1,
  },
  filename: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  size: {
    display: "block",
    color: tokens.colorNeutralForeground3,
  },
  statusSlot: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    gap: tokens.spacingHorizontalXS,
    flexShrink: 0,
  },
  details: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
    marginTop: tokens.spacingVerticalXS,
    borderTop: `1px dashed ${tokens.colorNeutralStroke2}`,
  },
  detailsWide: {
    display: "contents",
  },
  detailsHidden: {
    display: "none",
  },
  fieldLabel: {
    color: tokens.colorNeutralForeground3,
  },
  fieldLabelHidden: {
    display: "none",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
  noteTextWide: {
    gridColumn: "1 / -1",
  },
  matterOverridden: {
    fontWeight: tokens.fontWeightSemibold,
  },
});

const AttachmentRow: React.FC<AttachmentRowProps> = ({
  row,
  matters,
  mattersLoading,
  disabled,
  onToggle,
  onNameChange,
  onMatterOverrideChange,
  onRetry,
}) => {
  const styles = useStyles();
  const isWide = useIsWideLayout();
  const { icon, color } = getAttachmentIcon(row.originalName);

  // A row that's already uploaded, uploading, or of a type we can't process can't be selected or
  // edited — there's nothing left to change, and re-triggering an already-done upload would create
  // a duplicate.
  const isLocked = row.status === "success" || row.status === "uploading" || row.status === "unsupported";
  const isDisabled = disabled || isLocked;

  const detailsClassName = isWide ? styles.detailsWide : row.selected ? styles.details : styles.detailsHidden;

  return (
    <div className={mergeClasses(styles.row, isWide && styles.rowWide, !row.selected && !isLocked && styles.rowUnchecked)}>
      <div className={mergeClasses(styles.top, isWide && styles.topWide)}>
        <Checkbox checked={row.selected} disabled={isDisabled} onChange={(_, data) => onToggle(row.id, Boolean(data.checked))} />
        <div className={styles.iconBox} style={{ color }}>
          {icon}
        </div>
        <div className={styles.meta}>
          <Caption1 className={styles.filename}>{row.originalName}</Caption1>
          <Caption1 className={styles.size}>{formatFileSize(row.size)}</Caption1>
        </div>
        <div className={styles.statusSlot}>
          {row.status === "uploading" && <Spinner size="extra-tiny" />}
          {row.status === "success" && <Badge color="success">Done</Badge>}
          {row.status === "unsupported" && <Badge color="warning">Unsupported</Badge>}
          {row.status === "error" && (
            <>
              <Badge color="danger">Error</Badge>
              <Button appearance="outline" size="small" disabled={isDisabled} onClick={() => onRetry(row.id)}>
                Retry
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={detailsClassName}>
        <div className={styles.fieldGroup}>
          <Caption1 className={mergeClasses(styles.fieldLabel, isWide && styles.fieldLabelHidden)}>Name in system</Caption1>
          <Input size="small" value={row.customName} disabled={isDisabled} onChange={(_, data) => onNameChange(row.id, data.value)} />
        </div>
        <div className={styles.fieldGroup}>
          <Caption1 className={mergeClasses(styles.fieldLabel, isWide && styles.fieldLabelHidden)}>Matter</Caption1>
          <MatterCombobox
            className={row.matterOverrideId !== null ? styles.matterOverridden : undefined}
            size="small"
            matters={matters}
            loading={mattersLoading}
            selectedMatterId={row.matterOverrideId}
            onSelect={(matterId) => onMatterOverrideChange(row.id, matterId)}
            placeholder="Select a matter"
            defaultOptionLabel="Same as default"
            disabled={isDisabled}
          />
        </div>
      </div>

      {row.status === "error" && row.errorMessage && (
        <Caption1 className={mergeClasses(styles.errorText, isWide && styles.noteTextWide)}>{row.errorMessage}</Caption1>
      )}
    </div>
  );
};

export default AttachmentRow;
